import { Dispatch, SetStateAction, useCallback } from 'react';
import chroma from 'chroma-js';
import { RgbaColor } from 'react-colorful';

import { OnColorPickerChangeResult } from './color-picker.types';

import './rgba-inputs.css';

type RGBAInputsProps = {
  red: string;
  green: string;
  blue: string;
  alpha: string;
  setRed: Dispatch<SetStateAction<string>>;
  setGreen: Dispatch<SetStateAction<string>>;
  setBlue: Dispatch<SetStateAction<string>>;
  setAlpha: Dispatch<SetStateAction<string>>;
  debouncedChange?: (color: OnColorPickerChangeResult) => void;
  setColor: Dispatch<SetStateAction<{ hex: string; rgba: RgbaColor } | undefined>>;
};

const RGBAInputs = ({
  red,
  green,
  blue,
  alpha,
  setRed,
  setGreen,
  setBlue,
  setAlpha,
  debouncedChange,
  setColor,
}: RGBAInputsProps) => {
  const handleRGBAInputUpdate = useCallback(
    (label: string, value: string) => {
      let _red = red;
      let _green = green;
      let _blue = blue;
      let _alpha = alpha;
      switch (label) {
        case 'R':
          _red = value;
          break;
        case 'G':
          _green = value;
          break;
        case 'B':
          _blue = value;
          break;
        case 'A':
          _alpha = value;
          break;
      }
      const rgba = `rgba(${_red}, ${_green}, ${_blue}, ${_alpha})`;
      const hex = chroma(rgba).hex();
      setColor({
        rgba: {
          r: Number(_red),
          g: Number(_green),
          b: Number(_blue),
          a: Number(_alpha),
        },
        hex,
      });
      debouncedChange?.({
        rgba,
        hex,
        rgb: `rgb(${_red}, ${_green}, ${_blue})`,
        opacity: Number(_alpha),
      });
    },
    [red, green, blue, alpha, setColor, debouncedChange]
  );

  const handleOnInputChange = useCallback(
    (label: 'R' | 'G' | 'B', value: string) => {
      let setValue = setRed;
      switch (label) {
        case 'R':
          setValue = setRed;
          break;
        case 'G':
          setValue = setGreen;
          break;
        case 'B':
          setValue = setBlue;
          break;
      }
      if (!value) {
        setValue('');
        return;
      }
      const inRange = /\d{1,3}/.test(value);
      if (!inRange) return;
      const val = Number(value);
      if (val < 0 || val > 255) return;
      setValue(value);
      handleRGBAInputUpdate(label, value);
    },
    [handleRGBAInputUpdate, setBlue, setGreen, setRed]
  );

  const handleAlphaInputChange = useCallback(
    (value: string) => {
      if (!value) {
        setAlpha('');
        return;
      }
      // should match 0.00 to 1.00 and also allow 1 to be entered and 0 to be entered
      const inRange = /^(1(\.0{1,2})?|0(\.\d{1,2})?)$/.test(value);
      if (!inRange) return;
      handleRGBAInputUpdate('A', value);
      setAlpha(value);
    },
    [handleRGBAInputUpdate, setAlpha]
  );

  return (
    <div className="flex fd-row jc-center ai-center rgba-inputs-container">
      <div className="flex-column jc-center ai-center">
        <input
          id="color-picker-R"
          aria-label="Red"
          className="radius-100 clear-input text-center"
          type="number"
          min={0}
          value={red}
          max={255}
          step={1}
          onBlur={() => {
            if (!red) {
              setRed('0');
              handleRGBAInputUpdate('R', '0');
            }
          }}
          onChange={(e) => {
            handleOnInputChange('R', e.target.value);
          }}
        />
        <label className="width-100-percent flex fd-row jc-center ai-center" htmlFor="color-picker-R">
          R
        </label>
      </div>

      <div className="flex-column jc-center ai-center">
        <input
          id="color-picker-G"
          aria-label="Green"
          className="radius-100 clear-input text-center"
          type="number"
          min={0}
          value={green}
          max={255}
          step={1}
          onBlur={() => {
            if (!green) {
              setGreen('0');
              handleRGBAInputUpdate('G', '0');
            }
          }}
          onChange={(e) => {
            handleOnInputChange('G', e.target.value);
          }}
        />
        <label className="width-100-percent flex fd-row jc-center ai-center" htmlFor="color-picker-G">
          G
        </label>
      </div>

      <div className="flex-column jc-center ai-center">
        <input
          id="color-picker-B"
          aria-label="Blue"
          className="radius-100 clear-input text-center"
          type="number"
          min={0}
          value={blue}
          max={255}
          step={1}
          onBlur={() => {
            if (!blue) {
              setBlue('0');
              handleRGBAInputUpdate('B', '0');
            }
          }}
          onChange={(e) => {
            handleOnInputChange('B', e.target.value);
          }}
        />
        <label className="width-100-percent flex fd-row jc-center ai-center" htmlFor="color-picker-B">
          B
        </label>
      </div>

      <div className="flex-column jc-center ai-center">
        <input
          id="color-picker-A"
          aria-label="Alpha"
          className="radius-100 clear-input text-center"
          type="number"
          min={0}
          value={alpha}
          style={{ width: '48.5px' }}
          max={255}
          step={0.01}
          onBlur={() => {
            if (!alpha) {
              setAlpha('0');
              handleRGBAInputUpdate('A', '0');
            }
          }}
          onChange={(e) => {
            handleAlphaInputChange(e.target.value);
          }}
        />
        <label className="width-100-percent flex fd-row jc-center ai-center" htmlFor="color-picker-A">
          A
        </label>
      </div>
    </div>
  );
};

export default RGBAInputs;
